import type { MaybeRef } from "vue";
import { defu } from "defu";
import {
  isProbeUrl,
  unref,
  useNuxtApp,
  useCookie,
  useFetch,
  getCmsBaseUrl,
  useCurrentCulture,
  useUmbracoPreviewModeApiKey,
  useRequestHeaders,
  useUmbracoPreviewMode,
  apiQueryToKey,
  computed,
} from "#imports";

// Bodge to fix typing issues. useFetch should be typed, but the params parameter was not happy with the
// UseFetchOptions<T> typing.
export type UseApiFetchOptions<T> = Parameters<typeof useFetch<T>>[1];
export type UseApiFetchResponse<T> = ReturnType<typeof useFetch<T>>;

/**
 * Provides a wrapper around Nuxt's {@link useFetch} to facilitate data fetching to Apollo API endpoints.
 * The following functionality has been added:
 * - Logging and tracing
 * - De-duplicating identical concurrent API calls, e.g. 2x concurrent calls to /api/foo will only make one request
 * - Authentication with cookies & tokens
 * - Handling of cultures
 * - Handling of Umbraco's preview mode
 * - Prevent making API calls on the ReadyProbe page to save resources
 * @param url The URL to call
 * @param options Any {@link UseApiFetchOptions}
 */
export function useApiFetch<T> ( url: MaybeRef<string>, options: UseApiFetchOptions<T> = {} ): UseApiFetchResponse<T> {
  const userAuth = useCookie( "token" );
  const headerCookies = useRequestHeaders( ["cookie"] );

  const nuxtApp = useNuxtApp();

  const key = computed( () => {
    let result = unref( url );

    if ( options?.params ) {
      result += "_" + apiQueryToKey( options.params );
    }

    return result;
  } );

  const culture = useCurrentCulture();
  const { isPreviewMode } = useUmbracoPreviewMode();
  const previewModeApiKey = useUmbracoPreviewModeApiKey();
  const previewMode = isPreviewMode();

  const defaults: UseApiFetchOptions<T> = {
    baseURL: getCmsBaseUrl(),
    // cache request
    key: key.value,
    dedupe: "defer",

    onRequest ( { options } ) {
      options.headers = options.headers ?? new Headers();

      // @ts-ignore - headers is a Headers object
      options.headers["Accept-Language"] = culture.value;

      if ( previewMode ) {
        // @ts-ignore - headers is a Headers object
        options.headers.Preview = "true";

        // @ts-ignore - headers is a Headers object
        options.headers["Api-Key"] = previewModeApiKey.value;
      }

      // set user token if connected
      if ( userAuth.value ) {
        // @ts-ignore - headers is a Headers object
        options.headers.Authorization = `Bearer ${userAuth.value}`;
      }

      options.headers = {
        ...options.headers,
        ...headerCookies,
      };
    },
  };

  const params = defu( options, defaults );
  params.watch = [...params.watch || [], culture];

  const bypassProbeCheck = isProbeUrl( nuxtApp );

  if ( bypassProbeCheck ) {
    params.watch = undefined;
    params.immediate = false;
  }

  return useFetch<T>( url, params );
}
